/**
 * ThirdPartyScripts - Centralized management of third-party scripts
 *
 * This module manages all third-party script loading with optimal strategies
 * to minimize impact on Core Web Vitals, especially Total Blocking Time (TBT)
 *
 * Consent is NOT managed here — it is handled internally by GTM + Axeptio.
 * This module only controls WHEN scripts load (after user interaction) for
 * performance, not WHETHER they load (that's GTM's job via consent signals).
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5
 */

import { ScriptLoader } from '../utils/ScriptLoader.js'

export class ThirdPartyScripts {
  static config = {
    gtmId: null,
    axeptioId: null,
    environment: 'development',
  }

  static analyticsLoaded = false
  static liveChatLoaded = false

  static paintWaitState = {
    fcp: null,
    lcp: null,
  }

  /**
   * Initialize third-party scripts with configuration
   *
   * @param {Object} options - Configuration options
   * @param {string} [options.gtmId] - Google Tag Manager ID
   * @param {string} [options.axeptioId] - Axeptio client ID
   * @param {string} [options.environment='development'] - Environment (production/development)
   */
  static init(options = {}) {
    this.config = { ...this.config, ...options }

    // Only load third-party scripts in production
    if (this.config.environment !== 'production') {
      console.log('Third-party scripts disabled in development')
      return
    }

    // Set Axeptio configuration early so GTM's Axeptio tag can use it.
    // Axeptio SDK itself is loaded by a GTM tag — not by this module.
    if (this.config.axeptioId) {
      window.axeptioSettings = {
        clientId: this.config.axeptioId,
        cookiesVersion: this.getCookiesVersion(),
      }
    }

    // Load GTM and live chat after first user interaction.
    // Consent for individual tags (including Axeptio) is managed
    // within GTM, not gated here — we only defer for performance.
    ScriptLoader.waitForInteraction().then(() => {
      this.loadAnalytics()
      this.loadLiveChat()
    })
  }

  /**
   * Load Google Tag Manager
   * Strategy: Load after first user interaction to reduce TBT
   */
  static loadAnalytics() {
    if (this.analyticsLoaded) return
    if (!this.config.gtmId) return

    // Initialize dataLayer before GTM loads
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })

    this.analyticsLoaded = true

    ScriptLoader.load({
      src: `https://www.googletagmanager.com/gtm.js?id=${this.config.gtmId}`,
      id: 'google-tag-manager',
      async: true,
      strategy: 'immediate',
      onLoad: () => {
        this.trackPageView()
      },
      onError: (error) => {
        this.analyticsLoaded = false
        console.error('Failed to load Google Tag Manager:', error)
      },
    })
  }

  /**
   * Load Ekonsilio live chat
   * Strategy: Wait for FCP + LCP, then load immediately
   */
  static async loadLiveChat() {
    if (this.liveChatLoaded) return

    await this.waitForCriticalPaints()

    const locale = document.documentElement.lang
    let ekonsilioUrl = null
    switch (locale) {
      case 'fr':
        ekonsilioUrl = 'https://livechat.ekonsilio.io/6889f28d8e0b99bfa90d105f.js'
        break
      case 'en':
        ekonsilioUrl = 'https://livechat.ekonsilio.io/699470cd649cbfa4f3d8a0dc.js'
        break
      case 'nl':
        ekonsilioUrl = 'https://livechat.ekonsilio.io/69946fdede37e8ec6fbe5922.js'
        break
      case 'es':
        ekonsilioUrl = 'https://livechat.ekonsilio.io/699470320640f46a9c53659a.js'
        break
      case 'it':
        ekonsilioUrl = 'https://livechat.ekonsilio.io/699470620f3f6cfcc71c43c7.js'
        break
      case 'de':
        ekonsilioUrl = 'https://livechat.ekonsilio.io/6994709d9d42d81901901549.js'
        break
      default:
        ekonsilioUrl = 'https://livechat.ekonsilio.io/6889f28d8e0b99bfa90d105f.js'
        break
    }

    if (ekonsilioUrl) {
      this.liveChatLoaded = true
      ScriptLoader.load({
        src: ekonsilioUrl,
        id: 'ekonsilio-chat',
        defer: true,
        strategy: 'immediate',
        crossOrigin: 'anonymous',
        onLoad: () => {
          // Ekonsilio live chat loaded
        },
        onError: (error) => {
          this.liveChatLoaded = false
          console.error('Failed to load Ekonsilio live chat:', error)
        },
      })
    }
  }

  /**
   * Wait for both FCP and LCP (with safe fallbacks) before loading
   * non-critical UI overlays such as live chat.
   */
  static async waitForCriticalPaints() {
    await Promise.all([this.waitForFcp(), this.waitForLcp()])
  }

  static waitForFcp(maxWait = 5000) {
    if (this.paintWaitState.fcp) return this.paintWaitState.fcp

    this.paintWaitState.fcp = new Promise((resolve) => {
      let observer = null

      const done = () => {
        if (observer) observer.disconnect()
        resolve()
      }

      if (!window.performance) {
        done()
        return
      }

      const existingFcp = performance.getEntriesByName?.('first-contentful-paint') || []
      if (existingFcp.length) {
        done()
        return
      }

      if ('PerformanceObserver' in window) {
        try {
          observer = new PerformanceObserver((list) => {
            const hasFcp = list.getEntries().some((entry) => entry.name === 'first-contentful-paint')
            if (hasFcp) done()
          })
          observer.observe({ type: 'paint', buffered: true })
        } catch (error) {
          // Fallback to timeout when observer type is unsupported.
        }
      }

      setTimeout(done, maxWait)
    })

    return this.paintWaitState.fcp
  }

  static waitForLcp(maxWait = 6500) {
    if (this.paintWaitState.lcp) return this.paintWaitState.lcp

    this.paintWaitState.lcp = new Promise((resolve) => {
      const done = () => {
        if (observer) observer.disconnect()
        resolve()
      }

      if (!window.performance) {
        done()
        return
      }

      const existingLcp = performance.getEntriesByType?.('largest-contentful-paint') || []
      if (existingLcp.length) {
        done()
        return
      }

      let observer = null
      if ('PerformanceObserver' in window) {
        try {
          observer = new PerformanceObserver((list) => {
            if (list.getEntries().length) done()
          })
          observer.observe({ type: 'largest-contentful-paint', buffered: true })
        } catch (error) {
          // Fallback to timeout when observer type is unsupported.
        }
      }

      setTimeout(done, maxWait)
    })

    return this.paintWaitState.lcp
  }

  /**
   * Load Ausha podcast player when needed
   * Strategy: Load only when podcast embed is visible
   *
   * @param {string} [selector='.c-section-ausha'] - CSS selector for podcast section
   */
  static loadAushaPlayer(selector = '.c-section-ausha') {
    const aushaSection = document.querySelector(selector)
    if (!aushaSection) {
      return
    }

    ScriptLoader.loadOnVisible(selector, {
      src: 'https://player.ausha.co/ausha-player.js',
      id: 'ausha-player',
      async: true,
      onLoad: () => {
        // Ausha player loaded
      },
      onError: (error) => {
        console.error('Failed to load Ausha player:', error)
      },
    })
  }

  /**
   * Get cookies version based on locale
   *
   * @returns {string}
   */
  static getCookiesVersion() {
    const locale = document.documentElement.lang || 'fr'
    const localeMap = {
      fr: 'performanse-fr',
      en: 'performanse-en',
      nl: 'performanse-nl',
      es: 'performanse-es',
      it: 'performanse-it',
      de: 'performanse-de',
    }
    return localeMap[locale] || 'performanse-fr'
  }

  /**
   * Track page view in analytics
   */
  static trackPageView() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        page: {
          path: window.location.pathname,
          title: document.title,
          url: window.location.href,
        },
      })
    }
  }

  /**
   * Track custom event in analytics
   *
   * @param {string} eventName - Event name
   * @param {Object} [eventData={}] - Event data
   */
  static trackEvent(eventName, eventData = {}) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData,
      })
    }
  }

  /**
   * Preconnect to third-party domains
   * This should be called early to establish connections
   */
  static preconnectDomains() {
    const domains = [
      { href: 'https://www.googletagmanager.com', crossOrigin: true },
      { href: 'https://static.axept.io', crossOrigin: true },
      { href: 'https://js-eu1.hs-analytics.net', crossOrigin: true },
      { href: 'https://livechat.ekonsilio.io', crossOrigin: true },
    ]

    domains.forEach(({ href, crossOrigin }) => {
      ScriptLoader.preconnect(href, crossOrigin)
    })
  }
}
